import { createFileRoute } from "@tanstack/react-router";
import { useState, useMemo } from "react";
import { Card, CardContent } from "@/components/ui/card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { Loader2 } from "lucide-react";
import { ReportShell } from "@/features/reports/ReportShell";
import { useContracts } from "@/features/contracts/api";
import { useBranch } from "@/context/BranchContext";
import type { ReportFilters } from "@/features/reports/types";

export const Route = createFileRoute("/_authenticated/reports/amc-register")({
  component: AmcRegisterReport,
});

function AmcRegisterReport() {
  const [filters, setFilters] = useState<ReportFilters>({});
  const { currentBranchId } = useBranch();
  const { data: rows = [], isLoading } = useContracts({
    branchId: filters.branchId ?? currentBranchId ?? undefined,
  });


  const exportRows = useMemo(() => () => rows.map((r) => ({
    Contract: r.contract_no, Type: r.type, Vendor: r.vendor?.name ?? "",
    Start: r.start_date ?? "", End: r.end_date ?? "", Status: r.status, Value: r.contract_value ?? "",
    Assets: r.assets_count ?? 0,
  })), [rows]);

  return (
    <ReportShell id="R-07" title="AMC / CMC Contract Register"
      description="All service contracts with vendor and coverage details."
      filters={filters} onChange={setFilters}
      enabled={["branchId", "status"]} exportRows={exportRows}>
      <Card><CardContent className="p-0">
        {isLoading ? <div className="grid place-items-center py-10"><Loader2 className="h-5 w-5 animate-spin" /></div> : (
          <Table>
            <TableHeader><TableRow>
              <TableHead>Contract #</TableHead><TableHead>Type</TableHead><TableHead>Vendor</TableHead>
              <TableHead>Start</TableHead><TableHead>End</TableHead><TableHead>Status</TableHead>
              <TableHead className="text-right">Value</TableHead><TableHead className="text-right">Assets</TableHead>
            </TableRow></TableHeader>
            <TableBody>
              {rows.map((r) => (
                <TableRow key={r.id}>
                  <TableCell className="font-mono text-xs">{r.contract_no}</TableCell>
                  <TableCell>{r.type}</TableCell>
                  <TableCell>{r.vendor?.name ?? "—"}</TableCell>
                  <TableCell>{r.start_date ?? "—"}</TableCell>
                  <TableCell>{r.end_date ?? "—"}</TableCell>
                  <TableCell><Badge variant="secondary">{r.status}</Badge></TableCell>
                  <TableCell className="text-right tabular-nums">{r.contract_value?.toLocaleString("en-IN") ?? "—"}</TableCell>
                  <TableCell className="text-right tabular-nums">{r.assets_count ?? 0}</TableCell>
                </TableRow>
              ))}
              {rows.length === 0 && <TableRow><TableCell colSpan={8} className="py-8 text-center text-sm text-muted-foreground">No contracts.</TableCell></TableRow>}
            </TableBody>
          </Table>
        )}
      </CardContent></Card>
    </ReportShell>
  );
}
